import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiRepeat, FiUsers, FiGlobe, FiTrendingUp, FiWifiOff, FiExternalLink } from 'react-icons/fi';
import { FaFutbol } from 'react-icons/fa';
import StoreButtons from '../components/StoreButtons';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-emerald/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-emerald/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-emerald" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const WORLD = [
  { value: '7', label: 'countries' },
  { value: '14', label: 'divisions' },
  { value: '280', label: 'clubs' },
  { value: '100', label: 'manager portraits' },
];

const MatchdayEmpire = () => {
  const PRODUCT = getProduct('matchday-empire');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-emerald/10 border border-accent-emerald/20 text-accent-emerald font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FaFutbol} /> Football management · PC and Mac
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Matchday <span className="text-accent-emerald">Empire</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "Build a club people remember."
            </p>
            <div className="flex flex-col items-center justify-center gap-5">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" className="justify-center" />
              <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-white transition-colors">
                matchdayempire.com <SafeIcon icon={FiExternalLink} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pitch */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            A football manager the way they used to be made, with everything it needs today: squad, tactics, transfers,
            academy and a board that expects results. Pick a club in a world generated for your career, follow every
            match live on the pitch and answer the moments that decide it. Every matchday changes the story of your club.
          </p>
        </div>
      </section>

      {/* Trailer */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Trailer — Matchday Empire</p>
          <div className="border border-border bg-black">
            <video controls preload="metadata" playsInline poster="/reel/matchday-empire-1.webp" className="w-full block">
              <source src="/trailers/matchday-empire.mp4" type="video/mp4" />
            </video>
          </div>
          <p className="text-xs text-muted mt-3">All footage is real gameplay.</p>
        </div>
      </section>

      {/* Reel */}
      <section className="pb-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Matchday Empire</p>
          <Reel frames={PRODUCT.frames} label="Matchday Empire" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">Your club, your decisions.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiActivity} title="Every Match, Live" desc="Follow each game on a 2D pitch, change the plan mid-match and answer the key moments: a goal against, a red card, the opponent pushing." />
            <FeatureCard icon={FiRepeat} title="Transfers and Scouting" desc="Track talent, negotiate fees and contracts and balance the books. The other clubs sign, renew and strengthen too." />
            <FeatureCard icon={FiTrendingUp} title="Academy and Training" desc="Bring through youngsters, plan the sessions and grow your players season after season." />
            <FeatureCard icon={FiUsers} title="A Dressing Room with Character" desc="Leaders, mentors, prospects and hotheads. Every player has a personality, and the mood shows on the pitch." />
            <FeatureCard icon={FiGlobe} title="Play with Friends" desc="An online league for up to 8 managers, each with their own club, or 2 to 4 friends taking turns on the same team." />
            <FeatureCard icon={FiWifiOff} title="Yours, Offline" desc="The full career plays without internet. Saves stay on your computer. No ads, no telemetry." />
          </div>
        </div>
      </section>

      {/* World + specs */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="space-y-6">
              <h2 className="text-4xl font-black text-white uppercase italic">A World of <span className="text-accent-emerald">Its Own</span></h2>
              <p className="text-muted leading-relaxed">
                The world is generated from a seed: new clubs, squads, stadiums and competitions in every career.
                Men's or women's football, four difficulty levels from Apprentice to Survivor, and a world editor
                to build your own league and share it.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {WORLD.map((w) => (
                  <div key={w.label} className="p-4 bg-card border border-border rounded-md">
                    <div className="text-3xl font-black text-accent-emerald">{w.value}</div>
                    <p className="text-xs text-muted uppercase tracking-wider mt-1">{w.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-none p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase">Details</h3>
              <div className="space-y-4 text-sm">
                {[
                  ['Platform', 'Windows and macOS'],
                  ['Players', 'Single player · online league up to 8 · local 2-4'],
                  ['Controls', 'Mouse and keyboard'],
                  ['Languages', 'English, Spanish'],
                  ['Price', PRODUCT.priceNote],
                  ['Status', 'Out now on Microsoft Store'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-2 border-b border-border/50">
                    <span className="text-muted">{k}</span>
                    <span className="text-white text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" />
              </div>
            </div>
          </div>
          <p className="text-xs text-muted mt-12 max-w-4xl leading-relaxed">
            All MATCHDAY EMPIRE content is fictional. Clubs, crests, kits, players, managers, stadiums and competitions
            are generated by the game and do not represent any real organisation or person.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-emerald/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FaFutbol} className="w-16 h-16 text-accent-emerald mx-auto mb-8" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Every matchday changes the story</h2>
          <p className="text-muted mb-10 text-lg">
            One-time purchase. No microtransactions. No online requirement. Out now on the Microsoft Store,
            coming to Steam, Epic Games Store and the Mac App Store.
          </p>
          <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
             className="btn-primary bg-ember hover:bg-ember-bright text-white px-10 py-4 inline-flex decoration-transparent">
            Visit the MATCHDAY EMPIRE site
          </a>
        </div>
      </section>
    </div>
  );
};

export default MatchdayEmpire;
